import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import FRONTEND_CONFIG from "./frontend_config";
import { Download, Pencil, Trash2 } from "lucide-react";

const ExamQuestionSets = () => {
  const { examId } = useParams();
  const [questionSets, setQuestionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedSet, setExpandedSet] = useState(null);
  const [editingSet, setEditingSet] = useState(null);
  const [editName, setEditName] = useState("");
  const [toast, setToast] = useState(""); 
  const navigate = useNavigate();



const [showClassRangeModal, setShowClassRangeModal] = useState(false);
const [fromClass, setFromClass] = useState("");
const [toClass, setToClass] = useState("");
const [selectedSetId, setSelectedSetId] = useState("");

const handleAssignByClassRange = async () => {
  if (!fromClass || !toClass || !selectedSetId) {
    setToast("Please fill all fields!");
    setTimeout(() => setToast(""), 2500);
    return;
  }

  const classRange = `${fromClass}-${toClass}`;

  try {
    const res = await axios.post(`${FRONTEND_CONFIG.API_URL}/api/questionset/assign-by-class`, {
      fromClass,
      toClass,
      questionSetId: selectedSetId,
    });

   



    setToast(res.data.message || "Assigned successfully!");
    setTimeout(() => setToast(""), 2500);

    setShowClassRangeModal(false);
    setFromClass("");
    setToClass("");
    setSelectedSetId("");
  } catch (err) {
    console.error(err);
    setToast(err?.response?.data?.message || "Failed to assign by class range");
    setTimeout(() => setToast(""), 2500);
  }
};



  // -------------------------------
  // NEW STATES (Auto-suggest + Multi-select)
  // -------------------------------
  const [assigningSet, setAssigningSet] = useState(null);
  const [applicantId, setApplicantId] = useState("");
  const [applicantSearchResults, setApplicantSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [multiStudents, setMultiStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);




  // ---------------------------
  // Applicant search (single)
  // ---------------------------
  const handleApplicantSearch = async (value) => {
    setApplicantId(value);

    if (value.trim().length === 0) {
      setApplicantSearchResults([]);
      setShowDropdown(false);
      return;
    }

    try {
      const res = await axios.get(
        `${FRONTEND_CONFIG.API_URL}/api/applicant/search?query=${value}`
      );
      setApplicantSearchResults(res.data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  // ---------------------------
  // Multi-select search function
  // ---------------------------
  const searchStudents = async (value) => {
    if (value.trim().length === 0) return;

    try {
      const res = await axios.get(
        `${FRONTEND_CONFIG.API_URL}/api/applicant/search?query=${value}`
      );
      setMultiStudents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------------------
  // Assign Logic (per-set)
  // ---------------------------
  const handleAssign = async (mode) => {
    // -----------------------
    // Prevent duplicates
    // -----------------------
    if (mode === "specific" && assigningSet.assignedApplicants?.includes(applicantId)) {
      setToast("This applicant already has this question set!");
      setTimeout(() => setToast(""), 2500);
      return;
    }

    if (mode === "multiple") {
      const alreadyAssigned = selectedStudents.filter((id) =>
        assigningSet.assignedApplicants?.includes(id)
      );
      if (alreadyAssigned.length > 0) {
        setToast(`These applicants already have this set: ${alreadyAssigned.join(", ")}`);
        setTimeout(() => setToast(""), 2500);
        return;
      }
    }

    try {
      const payload = {
        questionSetId: assigningSet._id,
        mode,
      };

      if (mode === "specific") {
        payload.applicantId = applicantId;
      }

      if (mode === "multiple") {
        payload.applicantIds = selectedStudents;
      }

      const res = await axios.post(
        `${FRONTEND_CONFIG.API_URL}/api/questionset/assign`,
        payload
      );

      // ✅ Show response message as toast
      setToast(res.data.message);
      setTimeout(() => setToast(""), 2500);

      // -----------------------
      // Update local state
      // -----------------------
      setQuestionSets((prev) =>
        prev.map((s) =>
          s._id === assigningSet._id
            ? {
                ...s,
                assignedApplicants: [
                  ...(s.assignedApplicants || []),
                  ...(mode === "specific" ? [applicantId] : selectedStudents),
                ],
              }
            : s
        )
      );

      // Reset assign modal state
      setAssigningSet(null);
      setApplicantId("");
      setSelectedStudents([]);
    } catch (err) {
      console.error(err);
      setToast("❌ Failed to assign question set.");
      setTimeout(() => setToast(""), 2500);
    }
  };

  // ---------------------------
  // NEW: Assign ALL sets randomly (global)
  // ---------------------------
  const handleAssignAllRandom = async () => {
    if (!window.confirm("Are you sure? This will assign random sets to all students for this exam.")) {
      return;
    }

    try {
      const res = await axios.post(
        `${FRONTEND_CONFIG.API_URL}/api/questionset/assign-all-random`,
        { examId }
      );

      setToast(res.data.message || "All sets assigned randomly to students.");
      setTimeout(() => setToast(""), 2500);
    } catch (err) {
      console.error("Assign all random error:", err);
      const msg =
        err?.response?.data?.message ||
        "❌ Failed to assign all sets randomly. Check server logs.";
      setToast(msg);
      setTimeout(() => setToast(""), 2500);
    }
  };

  // ------------------------------
  // Fetch question sets
  // ------------------------------
  useEffect(() => {
    const fetchQuestionSets = async () => {
      try {
        const res = await axios.get(
          `${FRONTEND_CONFIG.API_URL}/api/questionset/exam/${examId}`
        );
        setQuestionSets(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch question sets.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionSets();
  }, [examId]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question set?"))
      return;

    try {
      await axios.delete(`${FRONTEND_CONFIG.API_URL}/api/questionset/${id}`);
      setQuestionSets((prev) => prev.filter((set) => set._id !== id));

      setToast("Question set deleted successfully ✅");
      setTimeout(() => setToast(""), 2500);
    } catch (err) {
      console.error("Error deleting question set:", err);
      setToast("❌ Failed to delete question set.");
      setTimeout(() => setToast(""), 2500);
    }
  };



  const handleEdit = (set) => {
    navigate(`/createquestion-set/${set._id}`, {
      state: { exam: { _id: set.examId, title: set.examName } },
    });
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${FRONTEND_CONFIG.API_URL}/api/questionset/${editingSet._id}`,
        { questionSetName: editName }
      );
      setQuestionSets((prev) =>
        prev.map((s) =>
          s._id === editingSet._id ? { ...s, questionSetName: editName } : s
        )
      );
      setEditingSet(null);

      setToast("Updated successfully");
      setTimeout(() => setToast(""), 2500);
    } catch (err) {
      setToast("Failed to update");
      setTimeout(() => setToast(""), 2500);
    }
  };

  // -----------------------------
  // PDF download
  // -----------------------------
  const handleDownloadPDF = (set) => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${set.examName} - ${set.questionSetName}`, 14, 20);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    let y = 30;

    set.questions.forEach((q, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(`${index + 1}. ${q.questionText}`, 14, y);
      y += 8;
      q.options.forEach((opt, i) => {
        doc.text(`   ${String.fromCharCode(65 + i)}. ${opt.optionText}`, 18, y);
        y += 7;
      });
      y += 5;
    });

    doc.save(`${set.questionSetName}_Questions.pdf`);
  };

  if (loading) return <div className="text-center py-10">Loading...</div>;
  if (error) return <div className="text-red-600 text-center">{error}</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-2xl font-bold mb-6 text-center text-blue-700">
        Question Sets for this Exam
      </h2>

      {/* NEW: Global random assign button (main page) */}
      <div className="flex justify-center mb-4">
        <button
          onClick={handleAssignAllRandom}
          className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded"
        >
          Assign All Sets Randomly
        </button>


{/*         
  <button
  onClick={() => setShowClassRangeModal(true)}
  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded ml-4"
>
  Assign by Class Range
</button> */}
<button
  onClick={() => setShowClassRangeModal(true)}
  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded ml-4"
>
  Assign by Class Range
</button>





        <button
          className="bg-gray-700 hover:bg-red-800 text-white px-4 py-2 rounded"
          onClick={() => navigate("/assigned-students")}
          style={{ marginLeft: "20px", padding: "10px" }}
        >
          View Assigned Students
        </button>
      </div>

      {/* Table */}
      {questionSets.length === 0 ? (
        <div className="text-center text-gray-600">
          ❌ No question sets found for this exam.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-300 bg-white shadow-md rounded-lg">
            <thead className="bg-blue-100 text-gray-700">
              <tr>
                <th className="p-2 border-b">Set Name</th>
                <th className="p-2 border-b text-center">Total</th>
                <th className="p-2 border-b">Created</th>
                <th className="p-2 border-b text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {questionSets.map((set) => (
                <React.Fragment key={set._id}>
                  <tr className="hover:bg-gray-50">
                    <td className="p-2 border-b">{set.questionSetName}</td>
                    <td className="p-2 border-b text-center">
                      {set.questions?.length || 0}
                    </td>
                    <td className="p-2 border-b text-gray-500">
                      {new Date(set.createdAt).toLocaleString()}
                    </td>

                    <td className="p-2 border-b flex justify-center gap-2">
                      <button
                        onClick={() =>
                          setExpandedSet(
                            expandedSet === set._id ? null : set._id
                          )
                        }
                        className="bg-blue-600 text-white px-3 py-1 rounded"
                      >
                        {expandedSet === set._id ? "Hide" : "View"}
                      </button>

                      <button
                        onClick={() => handleDownloadPDF(set)}
                        className="bg-green-600 text-white p-2 rounded"
                      >
                        <Download className="w-5 h-5" />
                      </button>

                      <button
                        onClick={() => handleEdit(set)}
                        className="bg-yellow-500 text-white p-2 rounded"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>

                      <button
                        onClick={() => handleDelete(set._id)}
                        className="bg-red-600 text-white p-2 rounded"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>

                      {/* Assign Button */}
                      <button
                        onClick={() => setAssigningSet(set)}
                        className="bg-purple-600 text-white p-2 rounded"
                      >
                        Assign
                      </button>
                    </td>
                  </tr>

                  {/* Expanded */}
                  {expandedSet === set._id && (
                    <tr>
                      <td colSpan="4" className="p-4 bg-gray-50">
                        <h3 className="font-semibold mb-3">
                          Questions in {set.questionSetName}
                        </h3>
                        {set.questions?.map((q, index) => (
                          <div
                            key={q._id}
                            className="p-3 border rounded bg-white mb-3"
                          >
                            <p className="font-semibold">
                              Q{index + 1}. {q.questionText}
                            </p>

                            <ul className="ml-4 mt-2">
                              {q.options.map((opt, i) => (
                                <li key={i}>{opt.optionText}</li>
                              ))}
                            </ul>

                            <p className="text-gray-600 mt-2">
                              Correct: {q.correctAnswer} | Level: {q.level} |
                              Marks: {q.marks}
                            </p>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      

{showClassRangeModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg w-96 shadow-lg">
      <h3 className="text-xl font-semibold mb-4 text-indigo-700">
        Assign Question Set by Class Range
      </h3>

      {/* From Class */}
      <label className="font-semibold">From Class</label>
      <input
        type="number"
        value={fromClass}
        onChange={(e) => setFromClass(e.target.value)}
        placeholder="e.g., 8"
        className="border rounded-md w-full px-3 py-2 mb-3"
      />

      {/* To Class */}
      <label className="font-semibold">To Class</label>
      <input
        type="number"
        value={toClass}
        onChange={(e) => setToClass(e.target.value)}
        placeholder="e.g., 12"
        className="border rounded-md w-full px-3 py-2 mb-3"
      />

      {/* Select Question Set */}
      <label className="font-semibold">Select Question Set</label>
      <select
        value={selectedSetId}
        onChange={(e) => setSelectedSetId(e.target.value)}
        className="border rounded-md w-full px-3 py-2 mb-4"
      >
        <option value="">-- Select Question Set --</option>
        {questionSets.map((set) => (
          <option key={set._id} value={set._id}>
            {set.questionSetName}
          </option>
        ))}
      </select>

      {/* Assign Button */}
      <button
        onClick={handleAssignByClassRange}
        className="w-full bg-indigo-600 text-white py-2 rounded mb-2"
      >
        Assign
      </button>

      {/* Cancel */}
      <button
        onClick={() => setShowClassRangeModal(false)}
        className="w-full bg-gray-400 text-white py-2 rounded"
      >
        Cancel
      </button>
    </div>
  </div>
)}






      {assigningSet && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96 shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-purple-700">
              Assign {assigningSet.questionSetName}
            </h3>

            {/* RANDOM ASSIGN */}
            <button
              onClick={() => handleAssign("random")}
              className="w-full bg-green-600 text-white py-2 rounded mb-3"
            >
              Assign Randomly
            </button>

            {/* SPECIFIC APPLICANT */}
            <input
              type="text"
              placeholder="Enter Application ID"
              value={applicantId}
              onChange={(e) => handleApplicantSearch(e.target.value)}
              className="border rounded-md w-full px-3 py-2"
            />

            {/* Auto-suggest dropdown */}
            {showDropdown && applicantSearchResults.length > 0 && (
              <div className="border rounded bg-white shadow max-h-40 overflow-y-auto">
                {applicantSearchResults.map((app) => (
                  <div
                    key={app._id}
                    onClick={() => {
                      setApplicantId(app.applicationId);
                      setShowDropdown(false);
                    }}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    {app.applicationId} — {app.studentName}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => handleAssign("specific")}
              className="w-full bg-blue-600 text-white py-2 rounded mt-2"
            >
              Assign to Applicant
            </button>

            {/* MULTI STUDENT SEARCH */}
            <h4 className="font-semibold mt-4 mb-2 text-purple-700">
              Assign to Multiple Students
            </h4>

            <input
              type="text"
              placeholder="Search students..."
              onChange={(e) => searchStudents(e.target.value)}
              className="border rounded-md w-full px-3 py-2"
            />

            <div className="max-h-40 overflow-y-auto border rounded mt-2">
              {multiStudents.map((stu) => (
                <label
                  key={stu._id}
                  className="flex items-center gap-2 p-2 border-b"
                >
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStudents((prev) => [
                          ...prev,
                          stu.applicationId,
                        ]);
                      } else {
                        setSelectedStudents((prev) =>
                          prev.filter((id) => id !== stu.applicationId)
                        );
                      }
                    }}
                  />
                  {stu.applicationId} — {stu.studentName}
                </label>
              ))}
            </div>

            <button
              onClick={() => handleAssign("multiple")}
              className="w-full bg-purple-700 text-white py-2 rounded mt-2"
            >
              Assign to Selected Students
            </button>

            <button
              onClick={() => setAssigningSet(null)}
              className="w-full bg-gray-400 text-white py-2 rounded mt-3"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingSet && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded w-96 shadow-lg">
            <h3 className="text-xl font-semibold mb-4">Edit Question Set</h3>

            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="border w-full px-3 py-2 rounded mb-4"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditingSet(null)}
                className="bg-gray-400 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleUpdate}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Toast for success/error messages */}
      {toast && (
        <div className="toast">{toast}</div>
      )}
    </div>
  );
};

export default ExamQuestionSets;
